import { FormControl, GridLegacy as Grid, useTheme } from "@mui/material";
import { StorageLocationLatLong } from "components/StorageLocationLatLong";
import { StateAbbreviation } from "encamp-shared/src/constants/states";
import {
  STORAGE_TYPE_DESCRIPTIONS,
  StorageType,
  disallowCustomStorageTypeStates,
  getStorageTypeDropdownOptionsForState,
  storageUnitMustMatchChemicalUnit,
} from "encamp-shared/src/constants/tierii";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { gql } from "generated-graphql";
import {
  FacilityChemicalStorageLocationInput,
  Temperature,
  UnitType,
} from "generated-graphql/graphql";
import React, { useEffect } from "react";
import { Controller, useFormContext, useFormState } from "react-hook-form";
import { useReport } from "routes/Customer/Chemicals/Report/useReport";
import { useFacility } from "routes/Customer/Facility/useFacility";

import { ErrorDisplay } from "../../../../../components/Forms/ErrorDisplay";
import { FormSelect } from "../../../../../components/Forms/FormSelect";
import { FormTextField } from "../../../../../components/Forms/FormTextField";
import { PressurePicker } from "../../../../../components/PressurePicker";
import { SearchSelect } from "../../../../../components/SearchSelect";
import { type ChemicalDensity, convertAmountsForUnitChange } from "./storageLocationUnitSync";
import { StorageTypeMultiSelect } from "./StorageTypeMultiSelect";

gql(`
  fragment ReportingFacilityChemicalStorageLocationForm on ReportingFacilityChemicalStorageLocation {
    id
    maxAmount
    averageAmount
    maxAmountLargestContainer
    storageLocationId
    pressure
    otherPressureValue
    storageType
    storageTypeDescription
    storageTypes
    temperature
    unit
    storageLocation {
      ...StorageLocationPicker
    }
  }
`);

gql(`
  fragment FacilityChemicalStorageLocationForm on FacilityChemicalStorageLocation {
    id
    maxAmount
    averageAmount
    maxAmountLargestContainer
    storageLocationId
    pressure
    otherPressureValue
    storageType
    storageTypeDescription
    storageTypes
    temperature
    unit
    storageLocation {
      ...StorageLocationPicker
    }
  }
`);

type StorageLocationFormProps = {
  disabled?: boolean;
  parentChemicalUnit?: UnitType | null;
  chemicalDensity?: ChemicalDensity;
};

export const FacilityChemicalStorageLocationForm: React.FC<StorageLocationFormProps> = ({
  disabled,
  parentChemicalUnit,
  chemicalDensity,
}) => {
  const theme = useTheme();
  const { data: reportData } = useReport();
  const { data: facilityData } = useFacility();
  const facilityState = facilityData?.facility?.state ?? reportData?.tierIIReport.facility.state;

  const unitDerivedFromChemical =
    storageUnitMustMatchChemicalUnit(facilityState) && parentChemicalUnit != null;

  const form = useFormContext<FacilityChemicalStorageLocationInput>();
  const { control, watch, setValue, getValues } = form;

  // Using useFormState ensures errors will re-render your component when errors change
  const { errors } = useFormState({ control });

  // Compliance requires the storage unit to equal the chemical unit in these
  // jurisdictions, so the field is derived: pull stale rows (including rows
  // saved before the field was locked) onto the parent unit. Converting the
  // amounts (density-aware) rather than merely relabeling is essential — a bare
  // relabel would silently change what the number means (100 gallons is not 100
  // pounds). When a weight<->volume change can't convert without a density, the
  // row is left truthful in its own unit and the unit-match rule surfaces it.
  const currentUnit = watch("unit");
  useEffect(() => {
    if (
      !unitDerivedFromChemical ||
      parentChemicalUnit == null ||
      currentUnit === parentChemicalUnit
    ) {
      return;
    }
    const { value, converted } = convertAmountsForUnitChange(
      {
        maxAmount: getValues("maxAmount"),
        averageAmount: getValues("averageAmount"),
        maxAmountLargestContainer: getValues("maxAmountLargestContainer"),
      },
      currentUnit,
      parentChemicalUnit,
      chemicalDensity
    );
    if (!converted) return;
    setValue("maxAmount", value.maxAmount ?? null);
    setValue("averageAmount", value.averageAmount ?? null);
    setValue("maxAmountLargestContainer", value.maxAmountLargestContainer ?? null);
    setValue("unit", parentChemicalUnit, { shouldValidate: true });
  }, [
    unitDerivedFromChemical,
    currentUnit,
    parentChemicalUnit,
    chemicalDensity,
    getValues,
    setValue,
  ]);

  const storageType = watch("storageType");
  const storageTypes = watch("storageTypes");

  // For CA the multi-select owns `storageTypes`; keep the legacy scalar
  // `storageType` synced to the first selected type (null when cleared). This
  // runs synchronously on change (see onTypesChange below) so a cleared
  // multi-select never leaves a stale storageType that the storageTypeFields
  // fallback would resurrect on save.
  const syncStorageTypeScalar = (types: string[]) => {
    setValue("storageType", types[0] ?? null, { shouldValidate: true });
  };
  const showsOtherDescription =
    facilityState === "CA"
      ? (storageTypes ?? []).includes(StorageType.Other)
      : storageType === StorageType.Other;
  const storageTypeDescriptions = facilityState
    ? getStorageTypeDropdownOptionsForState(facilityState as StateAbbreviation).map(
        (st) => st.label
      )
    : STORAGE_TYPE_DESCRIPTIONS;

  const allowsCustomStorageType = facilityState
    ? !disallowCustomStorageTypeStates.includes(facilityState)
    : true;

  return (
    <Grid container rowSpacing={theme.spacing(2)}>
      <Grid item xs={8} paddingRight={theme.spacing(1)}>
        <FormTextField
          name="maxAmount"
          label="Max Amount"
          control={control}
          disabled={disabled}
          hideErrors
        />
      </Grid>

      <Grid item xs={4} paddingLeft={theme.spacing(1)}>
        <FormSelect
          selectItems={Object.values(UnitType).map((unit) => ({
            display: prettyPrintEnumValue(unit),
            value: unit,
          }))}
          control={control}
          name="unit"
          label="Unit Type"
          disabled={disabled || unitDerivedFromChemical}
          tooltip={
            unitDerivedFromChemical
              ? "The unit is set on the chemical inventory record and applies to all of its storage locations. To change it, edit the Unit Type on the chemical."
              : undefined
          }
          helperText={unitDerivedFromChemical ? "Set on the chemical inventory record." : undefined}
          hideErrors
        />
      </Grid>

      {facilityState === "CA" && (
        <>
          <Grid item xs={8} paddingRight={theme.spacing(1)}>
            <FormTextField
              name="averageAmount"
              label="Average Amount"
              control={control}
              disabled={disabled}
              hideErrors
            />
          </Grid>

          <Grid item xs={4} paddingLeft={theme.spacing(1)} />

          <Grid item xs={8} paddingRight={theme.spacing(1)}>
            <FormTextField
              name="maxAmountLargestContainer"
              label="Largest Container Amount"
              control={control}
              disabled={disabled}
              hideErrors
            />
          </Grid>

          <Grid item xs={4} paddingLeft={theme.spacing(1)} />
        </>
      )}

      <Grid xs={12} paddingLeft={theme.spacing(2)}>
        <ErrorDisplay
          errors={errors}
          fields={
            facilityState === "CA"
              ? ["maxAmount", "averageAmount", "maxAmountLargestContainer", "unit"]
              : ["maxAmount", "unit"]
          }
        />
      </Grid>

      <Grid item xs={12}>
        {facilityState === "CA" ? (
          <>
            <StorageTypeMultiSelect
              control={control}
              disabled={disabled}
              onTypesChange={syncStorageTypeScalar}
            />
            <ErrorDisplay errors={errors} fields={["storageType", "storageTypes"]} />
          </>
        ) : (
          <Controller
            name={`storageType`}
            control={control}
            render={({ field, fieldState }) => (
              <FormControl fullWidth>
                <SearchSelect
                  {...field}
                  {...fieldState}
                  // Non-CA edits this single scalar, but `storageTypes` is the
                  // authoritative field downstream (storageTypeFields). Keep it
                  // synced so the edit isn't reverted to the stale loaded value.
                  onChange={(value) => {
                    field.onChange(value);
                    setValue("storageTypes", value ? [value] : [], {
                      shouldValidate: true,
                    });
                  }}
                  label="Storage Type"
                  required
                  options={Object.values(storageTypeDescriptions)}
                  getOptionLabel={(opt: any) => prettyPrintEnumValue(opt)}
                  disabled={disabled}
                />
              </FormControl>
            )}
          />
        )}
      </Grid>

      {showsOtherDescription && allowsCustomStorageType ? (
        <Grid item xs={12}>
          <FormTextField
            name="storageTypeDescription"
            label="Storage Type Description"
            control={control}
            disabled={disabled}
          />
        </Grid>
      ) : (
        <></>
      )}

      <Grid item xs={12}>
        <PressurePicker facilityState={facilityState} disabled={disabled} />
      </Grid>

      <Grid item xs={12}>
        <FormSelect
          name="temperature"
          label="Temperature"
          selectItems={Object.values(Temperature).map((temp) => ({
            display: prettyPrintEnumValue(temp),
            value: temp,
          }))}
          control={control}
          disabled={disabled}
        />
      </Grid>

      <Grid item xs={12}>
        <StorageLocationLatLong facilityState={facilityState} />
      </Grid>
    </Grid>
  );
};
